import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { request } from '../../../../config/helpers/axios-instance';
import { toast } from 'react-toastify';

function TransferPaymentsModal({ targetOrderId, onSuccess, onClose }) {
    const [sourceOrderId, setSourceOrderId] = useState('');
    const [deleteSourceOrder, setDeleteSourceOrder] = useState(true);
    const [deleteSourceQuotation, setDeleteSourceQuotation] = useState(true);
    const [confirming, setConfirming] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSubmit = () => {
        if (!sourceOrderId.trim()) {
            toast.error('Please enter a source order ID');
            return;
        }
        if (parseInt(sourceOrderId) === targetOrderId) {
            toast.error('Source and target order cannot be the same');
            return;
        }
        setConfirming(true);
    };

    const handleConfirm = async () => {
        setLoading(true);
        try {
            const response = await request({
                method: 'POST',
                url: 'userOrder/admin/transferPayments',
                data: {
                    sourceOrderId: parseInt(sourceOrderId),
                    targetOrderId,
                    deleteSourceOrder,
                    deleteSourceQuotation,
                },
            });

            if (response?.data?.success) {
                toast.success('Payments transferred successfully');
                onSuccess();
                onClose();
            } else {
                toast.error(response?.data?.err || 'Transfer failed');
                setConfirming(false);
            }
        } catch {
            toast.error('Something went wrong');
            setConfirming(false);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
            onClick={onClose}
        >
            <div
                className="bg-white-50 rounded-2xl shadow-xl p-8 w-full max-w-md flex flex-col gap-6"
                onClick={(e) => e.stopPropagation()}
            >
                <h2 className="text-xl font-bold text-gray-800">Transfer Payments</h2>
                <p className="text-sm text-gray-500">
                    Target Order ID: <span className="font-semibold text-gray-700">{targetOrderId}</span>
                </p>

                {!confirming ? (
                    <>
                        <div className="flex flex-col gap-2">
                            <label className="text-sm font-medium text-gray-600">Source Order ID</label>
                            <input
                                type="number"
                                value={sourceOrderId}
                                onChange={(e) => setSourceOrderId(e.target.value)}
                                className="border border-gray-300 rounded-xl px-4 py-2 text-gray-800 focus:outline-none focus:border-[#28A48B]"
                                placeholder="Enter source order ID..."
                            />
                        </div>

                        <div className="flex flex-col gap-3">
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={deleteSourceOrder}
                                    onChange={(e) => setDeleteSourceOrder(e.target.checked)}
                                    className="w-4 h-4 accent-[#28A48B]"
                                />
                                <span className="text-sm text-gray-700">Delete source order after transfer</span>
                            </label>
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={deleteSourceQuotation}
                                    onChange={(e) => setDeleteSourceQuotation(e.target.checked)}
                                    className="w-4 h-4 accent-[#28A48B]"
                                />
                                <span className="text-sm text-gray-700">Delete source quotation after transfer</span>
                            </label>
                        </div>

                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={onClose}
                                className="px-6 py-2 rounded-xl border border-gray-300 text-gray-600 font-semibold hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                className="px-6 py-2 rounded-xl bg-[#001524] text-white-50 font-semibold hover:opacity-90"
                            >
                                Transfer
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex flex-col gap-2">
                            <p className="text-sm font-semibold text-red-700">Are you sure?</p>
                            <p className="text-sm text-red-600">
                                All payment items and payments from order <span className="font-bold">{sourceOrderId}</span> will be moved to order <span className="font-bold">{targetOrderId}</span>. This cannot be undone.
                            </p>
                            {deleteSourceOrder && (
                                <p className="text-xs text-red-500">• Source order will be deleted</p>
                            )}
                            {deleteSourceQuotation && (
                                <p className="text-xs text-red-500">• Source quotation will be deleted</p>
                            )}
                        </div>

                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => setConfirming(false)}
                                disabled={loading}
                                className="px-6 py-2 rounded-xl border border-gray-300 text-gray-600 font-semibold hover:bg-gray-50 disabled:opacity-50"
                            >
                                Go Back
                            </button>
                            <button
                                onClick={handleConfirm}
                                disabled={loading}
                                className="px-6 py-2 rounded-xl bg-red-600 text-white-50 font-semibold hover:bg-red-700 disabled:opacity-50"
                            >
                                {loading ? 'Transferring...' : 'Confirm Transfer'}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

TransferPaymentsModal.propTypes = {
    targetOrderId: PropTypes.number.isRequired,
    onSuccess: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
};

export default TransferPaymentsModal;
